import { useState } from 'react';
import { Paperclip } from 'lucide-react';
import { Modale } from '../ui/Modale';
import { BoutonPrincipal, BoutonSecondaire, Carte, MessageErreur, Pastille } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { ChoixMotif, motifComplet } from '../absences/ChoixMotif';
import { ErreurApi } from '../../lib/api';
import {
  urlMonJustificatif,
  useDeposerJustificatif,
  useMesJustificatifs,
  useRetirerJustificatif,
  type VueJustification,
} from '../../lib/useJustificatifsAbsence';

function dateLisible(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' });
}

/**
 * « Justifier une absence » (maquette validée, espace apprenant) : le bouton,
 * et la fenêtre de dépôt — demi-journées, motif, fichier. Seules les absences
 * définitives encore non justifiées et sans dépôt en attente sont proposées :
 * c'est l'API qui les liste.
 */
export function BoutonJustifierAbsence(): JSX.Element | null {
  const { data } = useMesJustificatifs();
  const [ouverte, setOuverte] = useState(false);
  if (!data) return null;

  return (
    <>
      <BoutonSecondaire onClick={() => setOuverte(true)} disabled={data.aJustifier.length === 0}>
        Justifier une absence
      </BoutonSecondaire>
      {data.aJustifier.length === 0 && (
        <p className="text-[11px] text-codialis-gray">Aucune absence n’attend de justificatif.</p>
      )}
      {ouverte && <ModaleDepot vue={data} onFermer={() => setOuverte(false)} />}
    </>
  );
}

function ModaleDepot({ vue, onFermer }: { vue: VueJustification; onFermer: () => void }): JSX.Element {
  const toast = useToast();
  const deposer = useDeposerJustificatif();
  const [choisies, setChoisies] = useState<string[]>(
    vue.aJustifier.length === 1 && vue.aJustifier[0] ? [vue.aJustifier[0].presenceId] : [],
  );
  const [motif, setMotif] = useState('');
  const [precision, setPrecision] = useState('');
  const [fichier, setFichier] = useState<File | null>(null);

  const complet = choisies.length > 0 && motifComplet(vue.motifs, motif, precision) && fichier !== null;

  function basculer(presenceId: string): void {
    setChoisies((liste) =>
      liste.includes(presenceId) ? liste.filter((id) => id !== presenceId) : [...liste, presenceId],
    );
  }

  return (
    <Modale titre="Justifier une absence" sousTitre={`Formats acceptés : ${vue.formats}`} ouverte onFermer={onFermer}>
      <div className="space-y-4 p-4 sm:p-6">
        <fieldset className="space-y-2">
          <legend className="mb-1 text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
            Demi-journées couvertes par le justificatif
          </legend>
          <ul className="max-h-64 divide-y divide-gray-100 overflow-y-auto rounded-xl border border-gray-100">
            {vue.aJustifier.map((ligne) => (
              <li key={ligne.presenceId}>
                <label className="flex min-h-[44px] cursor-pointer items-center gap-3 px-3 py-2 text-sm text-codialis-navy">
                  <input
                    type="checkbox"
                    className="h-4 w-4 shrink-0 rounded"
                    checked={choisies.includes(ligne.presenceId)}
                    onChange={() => basculer(ligne.presenceId)}
                  />
                  <span className="min-w-0">
                    <span className="block first-letter:uppercase">{ligne.libelle}</span>
                    <span className="block text-[11px] text-codialis-gray">{ligne.seance}</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </fieldset>

        <ChoixMotif
          motifs={vue.motifs}
          motif={motif}
          precision={precision}
          onMotif={setMotif}
          onPrecision={setPrecision}
          prefixe="depot-justificatif"
        />

        <div>
          <label
            htmlFor="depot-justificatif-fichier"
            className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-codialis-gray"
          >
            Justificatif
          </label>
          <input
            id="depot-justificatif-fichier"
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.heic,application/pdf,image/jpeg,image/png,image/heic"
            onChange={(evenement) => setFichier(evenement.target.files?.[0] ?? null)}
            className="block w-full min-w-0 text-sm text-codialis-navy file:mr-3 file:min-h-[40px] file:rounded-lg file:border-0 file:bg-codialis-sand file:px-3 file:text-sm file:font-semibold file:text-codialis-navy"
          />
        </div>

        {deposer.error instanceof ErreurApi && <MessageErreur message={deposer.error.message} />}

        <div className="flex flex-wrap justify-end gap-2">
          <BoutonSecondaire onClick={onFermer}>Annuler</BoutonSecondaire>
          <BoutonPrincipal
            disabled={!complet || deposer.isPending}
            onClick={() =>
              fichier &&
              deposer.mutate(
                { motif, precision, presenceIds: choisies, fichier },
                {
                  onSuccess: () => {
                    toast('Justificatif déposé : le pôle pédagogique va l’examiner.');
                    onFermer();
                  },
                },
              )
            }
          >
            Déposer le justificatif
          </BoutonPrincipal>
        </div>
      </div>
    </Modale>
  );
}

/** Ses dépôts et leur examen : accepté, refusé avec le motif, en cours. */
export function MesJustificatifs(): JSX.Element | null {
  const toast = useToast();
  const { data } = useMesJustificatifs();
  const retirer = useRetirerJustificatif();
  if (!data || data.justificatifs.length === 0) return null;

  return (
    <Carte titre="Mes justificatifs d’absence" sansBordure>
      {retirer.error instanceof ErreurApi && (
        <div className="p-4 sm:px-6">
          <MessageErreur message={retirer.error.message} />
        </div>
      )}
      <ul className="divide-y divide-gray-100">
        {data.justificatifs.map((justificatif) => (
          <li key={justificatif.id} className="space-y-1.5 px-4 py-3 sm:px-6">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="min-w-0 text-sm font-semibold text-codialis-navy">{justificatif.motifLibelle}</span>
              <Pastille texte={justificatif.statutLibelle} ton={justificatif.statutTon} />
            </div>
            <div className="text-[11px] text-codialis-gray">
              Déposé le {dateLisible(justificatif.deposeLe)} · {justificatif.demiJournees.join(' · ')}
            </div>
            {justificatif.motifRefus && (
              <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-800">
                Motif du refus : {justificatif.motifRefus}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-3">
              <a
                href={urlMonJustificatif(justificatif.id)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex min-h-[40px] items-center gap-1.5 text-xs font-semibold text-codialis-sky hover:underline"
              >
                <Paperclip className="h-4 w-4" aria-hidden="true" />
                {justificatif.nomFichier}
              </a>
              {justificatif.refusRetrait === null && (
                <button
                  type="button"
                  disabled={retirer.isPending}
                  onClick={() =>
                    retirer.mutate(justificatif.id, { onSuccess: () => toast('Justificatif retiré.') })
                  }
                  className="min-h-[40px] text-xs font-semibold text-codialis-gray hover:underline disabled:opacity-50"
                >
                  Retirer
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </Carte>
  );
}
